import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { User } from '../types';
import { apiService, clearStoredAuth, isTokenExpired } from '../services/api';
import i18n from '../i18n/config';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (whatsappPhone: string, password: string) => Promise<User | false>;
  register: (username: string | undefined, email: string | undefined, password: string, role: 'EMPLOYER' | 'HELPER', whatsappPhone: string) => Promise<boolean>;
  updateProfile: (updates: Partial<User>) => Promise<boolean>;
  refreshUser: () => Promise<User | null>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // ── 同步從 storage 恢復使用者，確保首次渲染就有 user（避免 ProtectedRoute 閃「載入中…」）──
  const [user, setUser] = useState<User | null>(() => {
    try {
      const savedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!savedUser || !token || token.trim().length === 0) return null;
      if (isTokenExpired(token)) {
        clearStoredAuth();
        return null;
      }
      const parsed = JSON.parse(savedUser);
      if (!parsed || !parsed.id) return null;
      // 同步到 sessionStorage（跨標籤頁支援）
      if (!sessionStorage.getItem('user') && localStorage.getItem('user')) {
        sessionStorage.setItem('user', localStorage.getItem('user')!);
        sessionStorage.setItem('token', localStorage.getItem('token')!);
      }
      return parsed as User;
    } catch {
      return null;
    }
  });
  // loading 只在「沒有 cache」時為 true（需要等 server 驗證）；有 cache 時直接 false，不會閃爍
  const [loading, setLoading] = useState(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const savedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    return !token || !savedUser;
  });
  const isLoggingOutRef = React.useRef(false);

  // ── 提取 User 的關鍵欄位做比較，忽略 server 可能額外回傳的無關欄位差異 ──
  const userFingerprint = useCallback((u: User) => {
    return JSON.stringify({
      id: u.id,
      username: u.username,
      email: u.email,
      whatsappPhone: u.whatsappPhone || '',
      role: u.role,
      status: u.status,
      isVip: !!u.isVip,
      vipExpiryDate: u.vipExpiryDate || '',
      preferredLanguage: u.preferredLanguage || '',
    });
  }, []);

  useEffect(() => {
    // 背景靜默驗證 token + 同步最新資料（不影響初始渲染）
    if (isLoggingOutRef.current) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const savedUser = localStorage.getItem('user') || sessionStorage.getItem('user');

    // 沒有 token / user → 確保狀態乾淨
    if (!token || !savedUser || token.trim().length === 0 || isTokenExpired(token)) {
      setUser(null);
      clearStoredAuth();
      setLoading(false);
      return;
    }

    // 有 cache → 背景驗證（user 已在 useState 初始化中設好，不需再 setUser）
    (async () => {
      try {
        const response = await apiService.users.getProfile();
        if (response.success && response.data) {
          const fresh = response.data;
          // 只在「使用者關心的欄位」真正不同時才更新，避免不必要的重渲染/閃爍
          setUser(prev => {
            if (!prev) return fresh;
            if (userFingerprint(prev) === userFingerprint(fresh)) return prev; // 同一參考 = 不重渲染
            return fresh;
          });
          // 更新 cache
          const freshStr = JSON.stringify(fresh);
          sessionStorage.setItem('user', freshStr);
          localStorage.setItem('user', freshStr);
        } else {
          // Token 無效，清除認證資料
          clearStoredAuth();
          setUser(null);
        }
      } catch (authError: any) {
        // 僅在 401 未授權時清除認證，避免網路錯誤或 403 等導致誤登出
        const status = authError?.response?.status;
        if (status === 401) {
          clearStoredAuth();
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 同步用戶的偏好語言到 i18n（登入/刷新後自動套用）
  // ponytail: 只在用戶未手動選擇語言時才套用 preferredLanguage，避免覆蓋 localStorage 的明確選擇
  useEffect(() => {
    const explicitChoice = localStorage.getItem('languageExplicit');
    if (user?.preferredLanguage && !explicitChoice && user.preferredLanguage !== i18n.language) {
      i18n.changeLanguage(user.preferredLanguage);
    }
  }, [user?.preferredLanguage]);

  const login = useCallback(async (whatsappPhone: string, password: string): Promise<User | false> => {
    try {
      setLoading(true);

      const response = await apiService.auth.login({ whatsappPhone, password });

      if (response && response.success) {
        const { user: userData, token } = response.data;

        setUser(userData);
        // 同時存儲在 sessionStorage 和 localStorage（支援跨標籤頁）
        sessionStorage.setItem('user', JSON.stringify(userData));
        sessionStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', token);

        return userData;
      } else {
        throw new Error(response?.message || i18n.t('auth.loginFailed', '登入失敗'));
      }
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error details:', {
          message: error?.message,
          code: error?.code,
          status: error?.response?.status,
        });
      }
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (username: string | undefined, email: string | undefined, password: string, role: 'EMPLOYER' | 'HELPER', whatsappPhone: string): Promise<boolean> => {
    try {
      setLoading(true);

      const response = await apiService.auth.register({ username, email, password, role, whatsappPhone });

      if (response && response.success) {
        return !!(await login(whatsappPhone, password));
      } else {
        return false;
      }
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Register error details:', {
          message: error?.message,
          code: error?.code,
          status: error?.response?.status,
        });
      }
      // Re-throw with the actual error message from backend
      const errorMessage = error?.response?.data?.message || error?.message || i18n.t('auth.registerFailed', '註冊失敗');
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [login]);

  const updateProfile = useCallback(async (updates: Partial<User>): Promise<boolean> => {
    try {
      const response = await apiService.users.updateProfile(updates);
      if (response.success && response.data) {
        const userDataStr = JSON.stringify(response.data);
        setUser(response.data);
        // 更新 storage（支援跨標籤頁與避免快取資料不一致）
        sessionStorage.setItem('user', userDataStr);
        localStorage.setItem('user', userDataStr);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Update profile error:', error);
      return false;
    }
  }, []);

  const refreshUser = useCallback(async (): Promise<User | null> => {
    try {
      const response = await apiService.users.getProfile();
      if (response.success && response.data) {
        const userData = response.data;
        const userDataStr = JSON.stringify(userData);
        setUser(userData);
        // 更新 storage（支援跨標籤頁與避免快取資料不一致）
        sessionStorage.setItem('user', userDataStr);
        localStorage.setItem('user', userDataStr);
        return userData;
      }
      return null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Refresh user error:', error);
      }
      return null;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      isLoggingOutRef.current = true;
      
      // 立即清除用戶狀態和存儲
      setUser(null);
      clearStoredAuth();
      
      // 確保清除所有相關數據
      const remainingToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      const remainingUser = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (remainingToken || remainingUser) {
        clearStoredAuth();
      }
      
      // 確保 loading 狀態為 false，這樣 ProtectedRoute 可以立即檢測到沒有用戶
      setLoading(false);
    } catch (error) {
      setUser(null);
      setLoading(false);
      try {
        clearStoredAuth();
      } catch {
        // ignore storage clear errors
      }
    } finally {
      // 快速重置登出標誌
      setTimeout(() => {
        isLoggingOutRef.current = false;
      }, 100);
    }
  }, []);

  const value: AuthContextType = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    login,
    register,
    updateProfile,
    refreshUser,
    logout,
    loading
  }), [user, login, register, updateProfile, refreshUser, logout, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

